import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Check, CreditCard, User, Mail, Phone, Users, Clock, Sparkles, Tag, X, Shield, ShieldCheck, Lock, BadgeCheck, Banknote, Copy, CheckCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays } from "date-fns";
import { trackInitiateCheckout } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for trust badges
const getTrustBadgeIcon = (icon) => {
  switch (icon) {
    case 'shield': return <Shield className="w-4 h-4 text-green-500" />;
    case 'lock': return <Lock className="w-4 h-4 text-green-500" />;
    case 'credit-card': return <CreditCard className="w-4 h-4 text-green-500" />;
    case 'check': return <BadgeCheck className="w-4 h-4 text-green-500" />;
    default: return <Shield className="w-4 h-4 text-green-500" />;
  }
};

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedPackage = searchParams.get("package");

  const [packages, setPackages] = useState([]);
  const [packageSettings, setPackageSettings] = useState({});
  const [checkoutSettings, setCheckoutSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" or "interac"
  const [interacSettings, setInteracSettings] = useState(null);
  const [depositSettings, setDepositSettings] = useState({ deposit_percentage: 60 });
  const [interacBookingResult, setInteracBookingResult] = useState(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: null,
    event_type: "Wedding",
    package_id: preselectedPackage || "",
    guest_count: 50,
    special_requests: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch packages
        const packagesRes = await axios.get(`${API}/packages`);
        const bookablePackages = packagesRes.data.filter(p => p.price > 0);
        setPackages(bookablePackages);
        
        // Fetch package settings (for deposit info)
        try {
          const settingsRes = await axios.get(`${API}/admin/packages`);
          const settingsMap = {};
          settingsRes.data.forEach(pkg => {
            settingsMap[pkg.id] = {
              deposit_percentage: pkg.deposit_percentage,
              is_deposit_only: pkg.is_deposit_only
            };
          });
          setPackageSettings(settingsMap);
        } catch (e) {
          // Ignore if not available
        }
        
        // Fetch checkout settings for trust elements
        try {
          const siteSettingsRes = await axios.get(`${API}/site-settings`);
          if (siteSettingsRes.data?.checkout) {
            setCheckoutSettings(siteSettingsRes.data.checkout);
          }
        } catch (e) {
          // Ignore if not available
        }
        
        // Fetch Interac settings
        try {
          const interacRes = await axios.get(`${API}/admin/settings/interac`);
          setInteracSettings(interacRes.data);
        } catch (e) {
          // Ignore if not available
        }
        
        // Fetch deposit settings
        try {
          const depositRes = await axios.get(`${API}/settings/deposit`);
          setDepositSettings(depositRes.data);
        } catch (e) {
          // Ignore if not available
        }
        
        if (preselectedPackage) {
          setFormData(prev => ({ ...prev, package_id: preselectedPackage }));
        }
      } catch (error) {
        console.error("Error fetching packages:", error);
        toast.error("Failed to load packages");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [preselectedPackage]);

  const eventTypes = ["Wedding", "Corporate Event", "Birthday Party", "Private Party", "Anniversary", "Bridal Shower", "Baby Shower", "Team Building", "Other"];

  const selectedPackage = packages.find(p => p.id === formData.package_id);
  // Use global deposit percentage from settings, fallback to 60%
  const globalDepositPercentage = depositSettings.deposit_percentage || 60;
  const selectedSettings = packageSettings[formData.package_id];
  
  // Calculate deposit amount using global percentage as default for all experience packages
  // Only use package-specific settings if explicitly set (deposit_percentage !== 100 means it was customized)
  const hasCustomPackageSettings = selectedSettings && selectedSettings.deposit_percentage !== 100;
  const isDeposit = hasCustomPackageSettings ? (selectedSettings.is_deposit_only !== false) : true;
  const depositPercentage = hasCustomPackageSettings ? selectedSettings.deposit_percentage : globalDepositPercentage;
  const depositAmount = selectedPackage ? (selectedPackage.price * depositPercentage / 100) : 0;
  const balanceDue = selectedPackage ? (selectedPackage.price - depositAmount) : 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, event_date: date }));
  };

  const handlePackageSelect = (packageId) => {
    setFormData(prev => ({ ...prev, package_id: packageId }));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.package_id !== "";
      case 2:
        return formData.event_date !== null && formData.event_type !== "";
      case 3:
        return formData.name !== "" && formData.email !== "" && formData.phone !== "";
      default:
        return true;
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        type: "experiences"
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`Coupon applied: ${response.data.discount_value}${response.data.discount_type === 'percentage' ? '%' : ' CAD'} off!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid coupon code");
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  // Calculate discount
  const calculateDiscount = () => {
    if (!appliedCoupon || !selectedPackage) return 0;
    if (appliedCoupon.discount_type === "percentage") {
      return (selectedPackage.price * appliedCoupon.discount_value / 100);
    }
    return Math.min(appliedCoupon.discount_value, selectedPackage.price);
  };

  const discountAmount = calculateDiscount();
  const finalPrice = selectedPackage ? (selectedPackage.price - discountAmount) : 0;

  const handleSubmit = async () => {
    if (!isStepValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      // Meta: InitiateCheckout for experience booking
      trackInitiateCheckout({
        slug: 'experience',
        title: selectedPackage?.name || 'Experience',
        ticketType: selectedPackage?.name || '',
        quantity: parseInt(formData.guest_count) || 1,
        value: selectedPackage?.price || 0,
        currency: 'CAD',
        businessLine: 'experiences',
      });

      if (paymentMethod === "interac") {
        // Create Interac booking
        const response = await axios.post(`${API}/bookings/create-interac`, {
          ...formData,
          event_date: formData.event_date ? format(formData.event_date, "yyyy-MM-dd") : "",
          origin_url: window.location.origin,
          coupon_code: appliedCoupon?.code || null
        });
        
        // Show Interac confirmation with payment details
        setInteracBookingResult(response.data);
        setStep(5); // Move to Interac confirmation step
      } else {
        // Create Stripe checkout session
        const response = await axios.post(`${API}/bookings/create`, {
          ...formData,
          event_date: formData.event_date ? format(formData.event_date, "yyyy-MM-dd") : "",
          origin_url: window.location.origin,
          coupon_code: appliedCoupon?.code || null
        });

        // Redirect to Stripe checkout
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <main className="pt-20" data-testid="booking-page">
      {/* Hero */}
      <section className="section-padding-sm bg-[#050505] border-b border-[rgba(212,175,55,0.15)]">
        <div className="container-luxury">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
              Book Your Event
            </p>
            <h1 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
              Start Planning
            </h1>
            <p className="text-[#A1A1AA] text-lg">
              Complete your booking in a few simple steps. We'll take care of the rest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-[#0F0F0F] sticky top-20 z-40">
        <div className="container-luxury">
          <div className="flex justify-center gap-4 md:gap-8">
            {[
              { num: 1, label: "Package" },
              { num: 2, label: "Date & Event" },
              { num: 3, label: "Details" },
              { num: 4, label: "Payment" }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center transition-all ${
                    step > s.num
                      ? "bg-[#D4AF37] text-[#050505]"
                      : step === s.num
                      ? "border-2 border-[#D4AF37] text-[#D4AF37]"
                      : "border border-[rgba(212,175,55,0.3)] text-[#52525B]"
                  }`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`hidden md:block ml-3 text-sm uppercase tracking-wider ${
                  step >= s.num ? "text-[#F5F5F0]" : "text-[#52525B]"
                }`}>
                  {s.label}
                </span>
                {i < 3 && (
                  <div className={`hidden md:block w-12 h-px ml-4 ${
                    step > s.num ? "bg-[#D4AF37]" : "bg-[rgba(212,175,55,0.2)]"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding bg-[#050505]">
        <div className="container-luxury max-w-5xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <>
              {/* Step 1: Package Selection */}
              {step === 1 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                  data-testid="step-1"
                >
                  <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                    Select Your Package
                  </h2>
                  <div className="grid gap-4">
                    {packages.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => handlePackageSelect(pkg.id)}
                        className={`p-6 border cursor-pointer transition-all ${
                          formData.package_id === pkg.id 
                            ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)]" 
                            : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)] bg-[#121212]"
                        }`}
                        data-testid={`select-package-${pkg.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                                {pkg.name}
                              </h3>
                              <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                                formData.package_id === pkg.id
                                  ? "bg-[#D4AF37] border-[#D4AF37]"
                                  : "border-[rgba(212,175,55,0.3)]"
                              }`}>
                                {formData.package_id === pkg.id && (
                                  <Check className="w-4 h-4 text-[#050505]" />
                                )}
                              </div>
                            </div>
                            <p className="text-[#A1A1AA] text-sm mb-2">{pkg.tagline}</p>
                            <div className="flex flex-wrap gap-4 text-sm text-[#52525B]">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-[#D4AF37]" />
                                {pkg.duration}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4 text-[#D4AF37]" />
                                {pkg.guests}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-['Playfair_Display'] font-bold gold-text">
                              {pkg.price_display}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F]">
                    <p className="text-[#A1A1AA] text-sm">
                      <Sparkles className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                      Need a custom package or corporate pricing? <Link to="/contact" className="text-[#D4AF37] underline">Contact us</Link> for a personalized quote.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Date & Event Type */}
              {step === 2 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                  data-testid="step-2"
                >
                  <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                    Choose Your Date & Event Type
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-12">
                    {/* Calendar */}
                    <div>
                      <label className="form-label">Event Date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="w-full flex items-center gap-3 p-4 border border-[rgba(212,175,55,0.3)] bg-[#121212] text-left hover:border-[#D4AF37] transition-colors"
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
                            <span className={formData.event_date ? "text-[#F5F5F0]" : "text-[#52525B]"}>
                              {formData.event_date
                                ? format(formData.event_date, "MMMM d, yyyy")
                                : "Select a date"}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#121212] border-[rgba(212,175,55,0.3)]" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.event_date}
                            onSelect={handleDateSelect}
                            disabled={(date) => date < addDays(new Date(), 14)}
                            className="bg-[#121212]"
                            data-testid="calendar"
                          />
                        </PopoverContent>
                      </Popover>
                      <p className="text-[#52525B] text-sm mt-2">
                        Events must be booked at least 14 days in advance.
                      </p>
                    </div>

                    {/* Event Type */}
                    <div>
                      <label className="form-label">Event Type</label>
                      <div className="grid grid-cols-2 gap-3">
                        {eventTypes.map((type) => (
                          <button
                            key={type}
                            onClick={() => setFormData(prev => ({ ...prev, event_type: type }))}
                            className={`p-3 text-sm transition-all ${
                              formData.event_type === type
                                ? "bg-[#D4AF37] text-[#050505]"
                                : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:border-[#D4AF37]"
                            }`}
                            data-testid={`event-type-${type.toLowerCase().replace(' ', '-')}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>

                      <div className="mt-8">
                        <label className="form-label">Expected Guest Count</label>
                        <input
                          type="number"
                          name="guest_count"
                          value={formData.guest_count}
                          onChange={handleInputChange}
                          min="10"
                          max="500"
                          className="luxury-input"
                          data-testid="guest-count-input"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Personal Details */}
              {step === 3 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                  data-testid="step-3"
                >
                  <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                    Your Details
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <div className="relative">
                        <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          className="luxury-input pl-8"
                          required
                          data-testid="name-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className="luxury-input pl-8"
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Phone Number *</label>
                      <div className="relative">
                        <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="luxury-input pl-8"
                          required
                          data-testid="phone-input"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="form-label">Special Requests</label>
                      <textarea
                        name="special_requests"
                        value={formData.special_requests}
                        onChange={handleInputChange}
                        placeholder="Any dietary requirements, theme preferences, or special requests..."
                        rows={4}
                        className="luxury-input resize-none"
                        data-testid="special-requests-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Review & Payment */}
              {step === 4 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                  data-testid="step-4"
                >
                  <h2 className="text-2xl font-['Playfair_Display'] font-bold mb-8 text-[#F5F5F0]">
                    Review & Payment
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-12">
                    {/* Order Summary */}
                    <div className="luxury-card p-8">
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
                        Booking Summary
                      </h3>
                      
                      <div className="space-y-4 mb-8">
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Package</span>
                          <span className="text-[#F5F5F0]">{selectedPackage?.name}</span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Duration</span>
                          <span className="text-[#F5F5F0]">{selectedPackage?.duration}</span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Event Date</span>
                          <span className="text-[#F5F5F0]">
                            {formData.event_date ? format(formData.event_date, "MMMM d, yyyy") : "-"}
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Event Type</span>
                          <span className="text-[#F5F5F0]">{formData.event_type}</span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Guest Count</span>
                          <span className="text-[#F5F5F0]">{formData.guest_count}</span>
                        </div>
                        <div className="flex justify-between py-3 border-b border-[rgba(212,175,55,0.15)]">
                          <span className="text-[#A1A1AA]">Contact</span>
                          <span className="text-[#F5F5F0]">{formData.email}</span>
                        </div>
                      </div>
                      
                      {/* Coupon Code Section */}
                      <div className="mb-6 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                        <label className="text-[#D4AF37] text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Discount Code
                        </label>
                        {appliedCoupon ? (
                          <div className="flex items-center justify-between bg-[#121212] p-3 border border-green-600/30">
                            <div>
                              <span className="text-green-500 font-semibold">{appliedCoupon.code}</span>
                              <span className="text-[#A1A1AA] text-sm ml-2">
                                ({appliedCoupon.discount_value}{appliedCoupon.discount_type === 'percentage' ? '%' : ' CAD'} off)
                              </span>
                            </div>
                            <button
                              onClick={handleRemoveCoupon}
                              className="text-[#A1A1AA] hover:text-red-500"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Enter code"
                              className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none uppercase"
                              data-testid="coupon-input"
                            />
                            <button
                              onClick={handleApplyCoupon}
                              disabled={validatingCoupon || !couponCode.trim()}
                              className="btn-secondary px-4"
                              data-testid="apply-coupon-btn"
                            >
                              {validatingCoupon ? "..." : "Apply"}
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Price Breakdown */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Package Price</span>
                          <span className="text-[#F5F5F0]">C${selectedPackage?.price.toLocaleString()}</span>
                        </div>
                        {appliedCoupon && discountAmount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-500">Discount ({appliedCoupon.code})</span>
                            <span className="text-green-500">-C${discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center pt-4 border-t border-[rgba(212,175,55,0.15)]">
                        <span className="text-[#F5F5F0] font-semibold">
                          {isDeposit ? `${depositPercentage}% Deposit Due Today` : "Total Due Today"}
                        </span>
                        <span className="text-3xl font-['Playfair_Display'] font-bold gold-text">
                          C${isDeposit 
                            ? (finalPrice * depositPercentage / 100).toFixed(2) 
                            : finalPrice.toFixed(2)
                          }
                        </span>
                      </div>
                      
                      {isDeposit && (
                        <div className="mt-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-[#A1A1AA]">Package Price</span>
                            <span className="text-[#F5F5F0]">C${finalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-[#D4AF37] font-semibold">{depositPercentage}% Deposit (Pay Now)</span>
                            <span className="text-[#D4AF37] font-semibold">C${(finalPrice * depositPercentage / 100).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm pt-2 border-t border-[rgba(212,175,55,0.1)]">
                            <span className="text-[#A1A1AA]">{100 - depositPercentage}% Balance (Pay Later)</span>
                            <span className="text-[#F5F5F0]">C${(finalPrice - finalPrice * depositPercentage / 100).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-[#52525B] text-xs mt-2">
                        {isDeposit 
                          ? `*Pay ${depositPercentage}% now to secure your date. Balance due after we discuss your requirements and finalize pricing.`
                          : "*Final pricing customized based on guest count, location, and add-ons."
                        }
                      </p>
                    </div>

                    {/* Payment Info */}
                    <div>
                      {/* Trust Badges */}
                      {checkoutSettings?.trust_badges && checkoutSettings.trust_badges.length > 0 && (
                        <div className="mb-6 p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F]">
                          <div className="flex flex-wrap justify-center gap-4">
                            {checkoutSettings.trust_badges.map((badge, index) => (
                              <div key={index} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                                {getTrustBadgeIcon(badge.icon)}
                                <span>{badge.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Payment Method Selector */}
                      <div className="luxury-card p-6 mb-6">
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F0] mb-4">
                          Select Payment Method
                        </h3>
                        <div className="space-y-3">
                          {/* Credit Card Option */}
                          <button
                            onClick={() => setPaymentMethod("stripe")}
                            className={`w-full p-4 border transition-all flex items-center gap-4 ${
                              paymentMethod === "stripe"
                                ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)]"
                                : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)]"
                            }`}
                            data-testid="payment-method-stripe"
                          >
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              paymentMethod === "stripe" ? "border-[#D4AF37]" : "border-[#52525B]"
                            }`}>
                              {paymentMethod === "stripe" && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                            </div>
                            <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                            <div className="text-left flex-1">
                              <p className="text-[#F5F5F0] font-medium">Credit / Debit Card</p>
                              <p className="text-[#52525B] text-xs">Visa, Mastercard, Amex • Powered by Stripe</p>
                            </div>
                          </button>
                          
                          {/* Interac Option */}
                          {interacSettings?.enabled && (
                            <button
                              onClick={() => setPaymentMethod("interac")}
                              className={`w-full p-4 border transition-all flex items-center gap-4 ${
                                paymentMethod === "interac"
                                  ? "border-[#D4AF37] bg-[rgba(212,175,55,0.05)]"
                                  : "border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)]"
                              }`}
                              data-testid="payment-method-interac"
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                paymentMethod === "interac" ? "border-[#D4AF37]" : "border-[#52525B]"
                              }`}>
                                {paymentMethod === "interac" && <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />}
                              </div>
                              <Banknote className="w-5 h-5 text-[#D4AF37]" />
                              <div className="text-left flex-1">
                                <p className="text-[#F5F5F0] font-medium">Interac e-Transfer</p>
                                <p className="text-[#52525B] text-xs">Pay directly from your bank account</p>
                              </div>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Stripe Payment Info */}
                      {paymentMethod === "stripe" && (
                        <div className="luxury-card p-8 mb-6" data-testid="booking-secure-checkout-panel">
                          <div className="flex items-center gap-3 mb-4">
                            <ShieldCheck className="w-6 h-6 text-[#D4AF37]" strokeWidth={2.5} />
                            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                              Secure Payment by Stripe
                            </h3>
                          </div>
                          <p className="text-[#A1A1AA] text-sm leading-relaxed">
                            You'll be redirected to <span className="text-[#F5F5F0] font-semibold">Stripe's hosted checkout</span> at
                            <span className="font-mono text-xs mx-1 px-1.5 py-0.5 bg-black/40 rounded text-[#D4AF37]">checkout.stripe.com</span>
                            to enter your card. Your details are <span className="text-[#F5F5F0] font-semibold">never sent to or stored on our servers</span>.
                          </p>

                          {/* Trust grid */}
                          <div className="mt-5 grid grid-cols-2 gap-2.5">
                            {[
                              { icon: Lock, t: "256-bit TLS", s: "Encrypted in transit" },
                              { icon: Shield, t: "PCI-DSS Level 1", s: "Highest card standard" },
                              { icon: BadgeCheck, t: "3D Secure / SCA", s: "Bank-grade verification" },
                              { icon: CreditCard, t: "No card stored", s: "We never see your card" },
                            ].map((b, i) => (
                              <div key={i} className="flex items-start gap-2.5 p-3 border border-[rgba(212,175,55,0.18)] bg-[rgba(212,175,55,0.04)] rounded">
                                <b.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                                <div className="min-w-0">
                                  <p className="text-[#F5F5F0] text-xs font-semibold leading-tight">{b.t}</p>
                                  <p className="text-[#71717A] text-[10px] leading-tight mt-0.5">{b.s}</p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Card brand row */}
                          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-1.5">
                              {[
                                { l: "VISA", bg: "#1A1F71" },
                                { l: "MC", bg: "#EB001B", a: "#F79E1B" },
                                { l: "AMEX", bg: "#2E77BC" },
                                { l: "DISC", bg: "#FF6000" },
                                { l: "Pay", bg: "#000000" },
                                { l: "G Pay", bg: "#4285F4" },
                              ].map((c, i) => (
                                <div
                                  key={i}
                                  className="h-6 px-1.5 rounded-[5px] flex items-center justify-center text-[9px] font-black text-white tracking-wide shadow-sm relative overflow-hidden"
                                  style={{ backgroundColor: c.bg, minWidth: "32px" }}
                                  title={c.l}
                                >
                                  {c.a && (
                                    <span className="absolute right-0 top-0 bottom-0 w-1/2 opacity-90" style={{ backgroundColor: c.a }} />
                                  )}
                                  <span className="relative z-10">{c.l}</span>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-[rgba(99,91,255,0.4)] bg-[rgba(99,91,255,0.08)]">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA]">Powered by</span>
                              <span className="text-sm font-black tracking-tight text-[#635BFF]">stripe</span>
                            </div>
                          </div>

                          {/* Guarantee */}
                          {checkoutSettings?.guarantee_text && (
                            <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                              <p className="text-[#D4AF37] font-medium text-center">
                                {checkoutSettings.guarantee_text}
                              </p>
                            </div>
                          )}

                          {/* Money Back Guarantee */}
                          {checkoutSettings?.show_money_back && (
                            <p className="text-center text-[#A1A1AA] text-sm mt-2">
                              {checkoutSettings.money_back_days || 7}-Day Money-Back Guarantee
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Interac Payment Info */}
                      {paymentMethod === "interac" && interacSettings?.enabled && (
                        <div className="luxury-card p-8 mb-6">
                          <div className="flex items-center gap-3 mb-4">
                            <Banknote className="w-6 h-6 text-[#D4AF37]" />
                            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
                              Interac e-Transfer
                            </h3>
                          </div>
                          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                            {interacSettings.checkout_instructions || 
                              "After completing this form, you'll receive instructions to send your e-Transfer. Your booking will be confirmed once we receive your payment."}
                          </p>
                          
                          <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.2)] space-y-3">
                            <div>
                              <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Send e-Transfer to:</p>
                              <p className="text-[#D4AF37] font-semibold">{interacSettings.email}</p>
                            </div>
                            {interacSettings.security_question && (
                              <div>
                                <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Security Question:</p>
                                <p className="text-[#F5F5F0]">{interacSettings.security_question}</p>
                              </div>
                            )}
                            {interacSettings.security_answer && (
                              <div>
                                <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Security Answer:</p>
                                <p className="text-[#D4AF37] font-semibold">{interacSettings.security_answer}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F] mb-6">
                        <h4 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-3">
                          {isDeposit ? `${depositPercentage}% Deposit to Secure Your Date` : "Payment Terms"}
                        </h4>
                        <ul className="text-[#A1A1AA] text-sm space-y-2">
                          {isDeposit ? (
                            <>
                              <li>• <span className="text-[#F5F5F0] font-semibold">Pay {depositPercentage}% deposit today</span> to secure your date</li>
                              <li>• Balance of <span className="text-[#D4AF37]">C${(finalPrice - finalPrice * depositPercentage / 100).toFixed(2)}</span> due after we finalize your requirements</li>
                              <li>• We'll discuss your specific needs and confirm final pricing</li>
                              <li>• Your date is reserved once deposit is received</li>
                            </>
                          ) : (
                            <>
                              <li>• Full payment required to secure booking</li>
                              <li>• Retainers transferable within 6 months</li>
                            </>
                          )}
                        </ul>
                      </div>

                      {/* Terms Text */}
                      {checkoutSettings?.terms_text && (
                        <p className="text-[#52525B] text-xs mb-4 text-center">
                          {checkoutSettings.terms_text}
                        </p>
                      )}

                      <div className="text-[#52525B] text-sm space-y-2">
                        <p>• {checkoutSettings?.confirmation_email_note || "Confirmation email sent immediately after payment"}</p>
                        <p>• Our team will contact you within 24 hours</p>
                      </div>
                      
                      {/* Support Text */}
                      {checkoutSettings?.support_text && (
                        <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                          <p className="text-center text-[#A1A1AA] text-sm">
                            {checkoutSettings.support_text}
                          </p>
                        </div>
                      )}
                      
                      {/* Payment Methods */}
                      {checkoutSettings?.payment_methods && checkoutSettings.payment_methods.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                          <p className="text-center text-[#52525B] text-xs mb-3">We Accept</p>
                          <div className="flex justify-center flex-wrap gap-3">
                            {checkoutSettings.payment_methods.includes('visa') && (
                              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/visa.svg" alt="Visa" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                            )}
                            {checkoutSettings.payment_methods.includes('mastercard') && (
                              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/mastercard.svg" alt="Mastercard" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                            )}
                            {checkoutSettings.payment_methods.includes('amex') && (
                              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/americanexpress.svg" alt="American Express" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                            )}
                            {checkoutSettings.payment_methods.includes('discover') && (
                              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/discover.svg" alt="Discover" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                            )}
                            {checkoutSettings.payment_methods.includes('paypal') && (
                              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/paypal.svg" alt="PayPal" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" style={{filter: 'invert(1)'}} />
                            )}
                            {checkoutSettings.payment_methods.includes('interac') && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] rounded border border-[rgba(212,175,55,0.2)]">
                                <span className="text-[#A1A1AA] text-xs font-medium">Interac</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 5: Interac Confirmation */}
              {step === 5 && interacBookingResult && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                  data-testid="step-5"
                >
                  <div className="max-w-2xl mx-auto text-center">
                    <div className="w-20 h-20 bg-[#D4AF37] mx-auto mb-6 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-[#050505]" />
                    </div>
                    
                    <h2 className="text-3xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                      Almost There!
                    </h2>
                    <p className="text-[#A1A1AA] text-lg mb-8">
                      Your booking has been reserved. Complete your Interac e-Transfer to confirm.
                    </p>
                    
                    {/* Booking Reference */}
                    <div className="luxury-card p-6 mb-8">
                      <p className="text-[#52525B] text-sm uppercase tracking-wider mb-2">Your Booking Reference</p>
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl font-mono text-[#D4AF37] font-bold">
                          {interacBookingResult.booking_reference}
                        </span>
                        <button
                          onClick={() => copyToClipboard(interacBookingResult.booking_reference)}
                          className="p-2 border border-[rgba(212,175,55,0.3)] hover:border-[#D4AF37] transition-colors"
                          data-testid="copy-reference-btn"
                        >
                          <Copy className="w-4 h-4 text-[#D4AF37]" />
                        </button>
                      </div>
                      <p className="text-[#52525B] text-xs mt-2">Include this reference in your e-Transfer message</p>
                    </div>
                    
                    {/* Payment Instructions */}
                    <div className="luxury-card p-8 text-left mb-8">
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6 flex items-center gap-3">
                        <Banknote className="w-6 h-6 text-[#D4AF37]" />
                        Interac e-Transfer Instructions
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                          <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                            1
                          </div>
                          <div>
                            <p className="text-[#F5F5F0] font-medium mb-1">Send e-Transfer to:</p>
                            <div className="flex items-center gap-2">
                              <span className="text-[#D4AF37] font-semibold">{interacBookingResult.interac_email}</span>
                              <button
                                onClick={() => copyToClipboard(interacBookingResult.interac_email)}
                                className="text-[#52525B] hover:text-[#D4AF37]"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                          <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                            2
                          </div>
                          <div>
                            <p className="text-[#F5F5F0] font-medium mb-1">Amount to send:</p>
                            <span className="text-2xl text-[#D4AF37] font-bold">C${interacBookingResult.amount_due.toFixed(2)}</span>
                            {interacBookingResult.is_deposit && (
                              <p className="text-[#52525B] text-sm mt-1">
                                ({interacBookingResult.deposit_percentage}% deposit • Balance of C${interacBookingResult.balance_due.toFixed(2)} due later)
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {interacBookingResult.security_question && (
                          <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                            <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                              3
                            </div>
                            <div>
                              <p className="text-[#F5F5F0] font-medium mb-1">Security Question:</p>
                              <p className="text-[#A1A1AA]">{interacBookingResult.security_question}</p>
                              <p className="text-[#52525B] text-sm mt-2">Answer: <span className="text-[#D4AF37] font-semibold">{interacBookingResult.security_answer}</span></p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-start gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)]">
                          <div className="w-8 h-8 bg-[#D4AF37] text-[#050505] flex items-center justify-center font-bold flex-shrink-0">
                            {interacBookingResult.security_question ? "4" : "3"}
                          </div>
                          <div>
                            <p className="text-[#F5F5F0] font-medium mb-1">In the message, include:</p>
                            <p className="text-[#A1A1AA]">Your booking reference: <span className="text-[#D4AF37] font-mono font-bold">{interacBookingResult.booking_reference}</span></p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 border border-[rgba(212,175,55,0.2)] bg-[#0F0F0F] mb-8">
                      <p className="text-[#A1A1AA] text-sm">
                        <strong className="text-[#D4AF37]">What happens next?</strong><br />
                        Once we receive your e-Transfer, we'll send you a confirmation email and your booking will be secured. 
                        This usually takes 1-2 business hours during business days.
                      </p>
                    </div>
                    
                    <Link
                      to="/"
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="back-home-btn"
                    >
                      Back to Home
                    </Link>
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              {step < 5 && (
                <div className="flex justify-between mt-12 pt-8 border-t border-[rgba(212,175,55,0.15)]">
                  {step > 1 ? (
                    <button
                      onClick={() => setStep(step - 1)}
                      className="btn-secondary"
                      data-testid="prev-step-btn"
                    >
                      Previous
                    </button>
                  ) : (
                    <div />
                  )}

                  {step < 4 ? (
                    <button
                      onClick={() => isStepValid() && setStep(step + 1)}
                      className={`btn-primary ${!isStepValid() ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={!isStepValid()}
                      data-testid="next-step-btn"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !isStepValid()}
                      className={`btn-primary flex items-center gap-2 ${
                        submitting || !isStepValid() ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      data-testid="submit-booking-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : paymentMethod === "interac" ? (
                        <>
                          <Banknote className="w-5 h-5" />
                          Complete Booking
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          Proceed to Payment
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default BookingPage;
